import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddLeadDialog({ open, onClose, lead, onSaved }) {
  const { token } = useAuth();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [position, setPosition] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setName(lead.name || '');
      setPhone(lead.phone || '');
      setEmail(lead.email || '');
      setPosition(lead.position || '');
      setNotes(lead.notes || '');
    } else {
      setName(''); setPhone(''); setEmail(''); setPosition(''); setNotes('');
    }
  }, [lead, open]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error('Name und Telefon sind Pflichtfelder');
      return;
    }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const data = { name: name.trim(), phone: phone.trim(), email, position, notes };
      if (lead) {
        await axios.put(`${API}/leads/${lead.id}`, data, { headers });
        toast.success('Lead aktualisiert');
      } else {
        await axios.post(`${API}/leads`, data, { headers });
        toast.success('Lead erstellt');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
            {lead ? 'Lead bearbeiten' : 'Neuer Lead'}
          </DialogTitle>
          <DialogDescription>
            {lead ? 'Aktualisiere die Lead-Daten.' : 'Füge einen neuen Lead/Bewerber hinzu.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="lead-name">Name *</Label>
            <Input
              id="lead-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Max Mustermann"
              className="rounded-sm"
              data-testid="lead-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-phone">Telefon *</Label>
            <Input
              id="lead-phone"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="+49 170 1234567"
              className="rounded-sm font-mono"
              data-testid="lead-phone-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-email">E-Mail</Label>
            <Input
              id="lead-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="email@beispiel.de"
              className="rounded-sm"
              data-testid="lead-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-position">Position</Label>
            <Input
              id="lead-position"
              value={position}
              onChange={e => setPosition(e.target.value)}
              placeholder="z.B. Softwareentwickler"
              className="rounded-sm"
              data-testid="lead-position-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-notes">Notizen</Label>
            <Textarea
              id="lead-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optionale Notizen..."
              className="rounded-sm"
              data-testid="lead-notes-input"
            />
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm" data-testid="cancel-lead-button">
              Abbrechen
            </Button>
            <Button type="submit" disabled={saving} className="rounded-sm bg-black text-white hover:bg-slate-800" data-testid="save-lead-button">
              {saving ? 'Speichern...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
